import CryptoJS from 'crypto-js';

const EXPIRATION_IN_DAYS = 30;
const COOKIE_NAME = "userCredentials";


export const useAutoFillCredentials = (secretKey: string) => {
  /**
   * Encripta un string usando AES
   */
  const encrypt = (text: string) => {
    try {
      return CryptoJS.AES.encrypt(text, secretKey).toString();
    } catch (error) {
      console.error('Error en encriptación:', error);
      throw new Error('Error al encriptar datos');
    }
  }

  /**
   * Desencripta un string usando AES
   */
  const decrypt = (encryptedData: string) => {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, secretKey);

      return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Error en desencriptación:', error);
      throw new Error('Error al desencriptar datos');
    }
  }

  /**
   * Guarda datos encriptados en una cookie
   */
  const setSecureCookie = (
    name: string,
    value: string,
    expirationDays = EXPIRATION_IN_DAYS,
    path = '/'
  ) => {
    try { 
      const encryptedValue = encrypt(value);

      // Calcular la fecha de expiración
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + expirationDays);
      
      // Crear la cookie con opciones de seguridad
      const cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(encryptedValue)}; ` +
        `expires=${expirationDate.toUTCString()}; ` +
        `path=${path}; ` +
        'SameSite=Strict; ' + // Protección contra CSRF
        'Secure;'; // Solo HTTPS

      document.cookie = cookieString;
    } catch (error) {
      console.error('Error al guardar cookie encriptada:', error);
      throw new Error('No se pudo guardar la cookie encriptada');
    }
  }

  /**
   * Obtiene y desencripta el valor de una cookie
   */
  const getSecureCookie = (name: string) => {
    try {
      const cookies = document.cookie
        .split(';')
        .map(cookie => cookie.trim());

        const cookieString = cookies
        .find(cookie => cookie.startsWith(`${name}=`));

      if (!cookieString) {
        return null;
      }

      const encryptedValue = decodeURIComponent(
        cookieString.split('=')[1]
      );

      return decrypt(encryptedValue);
    } catch (error) {
      console.error('Error al leer cookie encriptada:', error);
      return null;
    }
  }

  /**
   * Elimina una cookie específica
   */
  const deleteSecureCookie = (name: string, path = '/') => {
    document.cookie = `${
      name
    }=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${
      path
    }; Secure; SameSite=Strict`;
  }

  // Eliminar credenciales
  const clearCredentials = () => {
    try {
      deleteSecureCookie(COOKIE_NAME);
    } catch (error) {
      console.error('Error al eliminar credenciales:', error);
    }
  }

  // Recuperar credenciales
  const getCredentials = () => {
    try {
      const credentialsStr = getSecureCookie(COOKIE_NAME);

      if (credentialsStr) {
        return JSON.parse(credentialsStr);
      }
      return null;
    } catch (error) {
      console.error('Error al recuperar credenciales:', error);
      return null;
    }
  }

  const saveCredentials = (email: string, password: string, shouldSaveCredential: boolean) => {
    try {
      if (shouldSaveCredential) {
        const credentials = JSON.stringify({ email, password });
        setSecureCookie(COOKIE_NAME, credentials, EXPIRATION_IN_DAYS);
      } else {
        const credentials = getCredentials();
        if (email === credentials?.email) clearCredentials();
      }
    } catch (error) {
      console.error('Error al guardar credenciales:', error);
    }
  }

  return { saveCredentials, getCredentials }
}
